import { isCsr } from '@fe/armor_utils';
import React from 'react';
import { onelinkMap } from './constants';
/**
 * Simple query string parser.
 *
 * @param {String} query The query string that needs to be parsed.
 * @returns {Object}
 * @api public
 */

export function querystring(query: string): Record<string, string> {
  const parser = /([^=?#&]+)=?([^&]*)/g;
  const result: Record<string, string> = {};
  let part: RegExpExecArray | null = null;

  while (part = parser.exec(query)) {
    const key = decodeURIComponent(part[1]);
    const value = decodeURIComponent(part[2]);
    if (key === null || value === null || key in result) continue;
    result[key] = value;
  }

  return result;
}
/**
 * Transform a query string to an object.
 *
 * @param {Object} obj Object that should be transformed.
 * @param {String} prefix Optional prefix.
 * @returns {String}
 * @api public
 */

export function querystringify(obj: Record<string, string | number>): string {
  const pairs: string[] = [];
  let value: string | number;
  let key: string;

  for (key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      value = obj[key];

      if (!value && (value === null || typeof value === 'undefined')) {
        // value = '';
        continue;
      }

      key = encodeURIComponent(key);
      value = encodeURIComponent(value); //
      // If we failed to encode the strings, we should bail out as we don't
      // want to add invalid strings to the query.
      //

      if (key === null || value === null) continue;
      pairs.push(key + '=' + value);
    }
  }

  return pairs.length ? pairs.join('&') : '';
}
export function insertDomToStarlingKey(targetStr: string, placeholder: string, ReactElement: React.ReactElement): [string, React.ReactElement, string] | string {
  if (!targetStr.includes(placeholder)) return targetStr;
  const [beforeStr, afterStr] = targetStr.split(placeholder); // fix `Each child in a list should have a unique "key" prop.` error

  const withKeyElement = ReactElement.key ? ReactElement : React.cloneElement(ReactElement, {
    key: '0',
    ...ReactElement.props
  }, ReactElement.props.children);
  return [beforeStr, withKeyElement, afterStr];
}

const getPluralStarlingConfig = (sourceStr: string) => {
  let sourceStrAfter = sourceStr.replace('{num, plural, ', '');
  sourceStrAfter = sourceStrAfter.replace(/\}$/, '');
  const bracketList: string[] = [];
  const bracketPosition: number[][] = [];

  for (let i = 0; i < sourceStrAfter.length; i++) {
    const itemStr = sourceStrAfter[i];

    if (itemStr === '{') {
      if (bracketList.length === 0) {
        bracketPosition.push([i]);
      }

      bracketList.push(itemStr);
    } else if (itemStr === '}') {
      bracketList.pop();

      if (bracketList.length === 0) {
        const current = bracketPosition[bracketPosition.length - 1];
        current[1] = i;
      }
    }
  }

  let lastPosition = 0;
  const config = {};
  bracketPosition.forEach(([start, end]) => {
    const key = sourceStrAfter.slice(lastPosition, start).trim();
    const value = sourceStrAfter.slice(start + 1, end); // remove bracket

    lastPosition = end + 1;
    config[key] = value;
  });
  return config;
};

const getStrBasedOnQuantifiers = (num: string | number, config: Record<string, string>, lng: string) => {
  const quantifierMap = {
    '0': 'zero',
    '1': 'one',
    '2': 'two',
    '3': 'three'
  };
  const quantifier = quantifierMap[num];

  if (config[quantifier] !== undefined) {
    return config[quantifier];
  }

  if (['ar', 'ar-AE', 'ar-EG', 'ar-MA', 'ar-SA'].indexOf(lng) != -1) {
    if (Number(num) <= 10 && config['few']) {
      return config['few'];
    } else if (Number(num) % 100 === 0 && config['other']) {
      return config['other'];
    } else if (config['many']) {
      return config['many'];
    }
  }

  return config['other'];
};

export type TFunction = (key: string, defaultStr?: string, opt?: Record<string, string | number | undefined>) => string;
export const translationFactory = (starling: Record<string, string>, lng?: string): TFunction => {
  return (key: string, defaultStr = '', opt) => {
    let str = starling[key] || defaultStr;

    if (str.startsWith('{num, plural,') && opt && opt.num !== undefined) {
      const config = getPluralStarlingConfig(str);
      str = getStrBasedOnQuantifiers(opt.num, config, lng || 'en');
    }

    if (opt) {
      Object.keys(opt).forEach(key => {
        str = str.replace(new RegExp(`{${key}}`, 'g'), opt[key] !== undefined ? `${opt[key]}` : '');
      });
    }

    return str;
  };
};
export const advancedTranslation = (starling: Record<string, string>): TFunction => {
  return (key: string, defaultStr = '', opt) => {
    let str = starling[key] || defaultStr;
    const regOne = /one\s{.*?}\sother/g;
    const regOther = /other\s{.*?}}/g;
    const tempStrOne = str.match(regOne);
    const tempStrOther = str.match(regOther);

    if (Number(opt?.[0]) === 1 && tempStrOne) {
      str = tempStrOne[0].slice(7, tempStrOne[0].length - 2);
    } else if (tempStrOther) {
      str = tempStrOther[0].slice(7, tempStrOther[0].length - 2);
    }

    if (opt) {
      Object.keys(opt).forEach(key => {
        str = str.replace(new RegExp(`{${key}}`, 'g'), opt[key] !== undefined ? `${opt[key]}` : '');
      });
    }

    return str;
  };
};
export const isDev = () => {
  return process.env.NODE_ENV === 'development';
};
export const checkInApp = (ua?: string): boolean => {
  if (typeof window === 'undefined' && !ua) {
    return false;
  }

  const userAgent = ua || navigator.userAgent;
  const byteLocale = /ByteLocale\//g;
  return Boolean(userAgent.match(byteLocale));
};
export function getAppRegion(ua: string) {
  const region = /region\/([\w.]+)/gi.exec(ua);

  if (region) {
    return region[1].toLowerCase();
  }

  return null;
}
export const throttle = (fn: Function, wait: number) => {
  let isCalled = false;
  return function (...args) {
    if (!isCalled) {
      fn(...args);
      isCalled = true;
      setTimeout(function () {
        isCalled = false;
      }, wait);
    }
  };
};
export const isInApp = (): boolean => {
  if (!isCsr()) {
    throw new Error('func only work in csr');
  }

  const userAgent = navigator.userAgent;
  const byteLocale = /ByteLocale\//g;
  return Boolean(userAgent.match(byteLocale));
};
export const isRTL = (query: Record<string, string> = {}) => {
  const {
    dir,
    lng
  } = query;
  return dir === 'rtl' || ['ar', 'he', 'ur'].some(key => lng?.startsWith(key));
};
export const isCardCollectionPage = () => {
  return location.pathname === '/activity/armor/card-collection';
};
export const checkOverflow = (element: HTMLElement): boolean => {
  if (!element) return false;
  const curOverflow = element.style.overflow;

  if (!curOverflow || curOverflow === 'visible') {
    element.style.overflow = 'hidden';
  }

  const isOverflowing = element.clientWidth < element.scrollWidth;
  element.style.overflow = curOverflow;
  return isOverflowing;
};
export const checkVerticalOverflow = (element: HTMLElement): boolean => {
  if (!element) return false;
  const curOverflow = element.style.overflow;

  if (!curOverflow || curOverflow === 'visible') {
    element.style.overflow = 'hidden';
  }

  const isOverflowing = element.clientHeight < element.scrollHeight;
  element.style.overflow = curOverflow;
  return isOverflowing;
};
export function scrollToModule(module_id: string): void {
  const ele = document.getElementById(module_id);

  if (ele) {
    const viewPortHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    const top = viewPortHeight / 2;
    const eleTop = ele.offsetTop;
    setTimeout(() => {
      scrollSmoothTo(Math.abs(eleTop - top));
    }, 100);
  }
}

const scrollSmoothTo = function (position: number): void {
  if (!window.requestAnimationFrame) {
    window.scrollTo({
      behavior: 'smooth',
      top: position
    });
  }

  let scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

  const step = function () {
    const distance = position - scrollTop;
    scrollTop = scrollTop + distance / 6;

    if (Math.abs(distance) < 1) {
      window.scrollTo(0, position);
    } else {
      window.scrollTo(0, scrollTop);
      requestAnimationFrame(step);
    }
  };

  step();
};

export const safetyJSONParse = (str: string | null) => {
  if (!str) return;

  try {
    return JSON.parse(str);
  } catch {}

  return;
};
export const createArr = (n: number) => new Array(n).fill(0);
type oneLinkProps = {
  query: Record<string, string>;
  activity_url: string;
  pid: string;
  gd_label: string;
  invite_code?: string;
  external_query?: Record<string, string>;
};
export const getOneLink = ({
  query,
  external_query,
  activity_url,
  pid,
  invite_code,
  gd_label
}: oneLinkProps) => {
  const {
    aid,
    region,
    u_code,
    enter_from
  } = query;
  const config = onelinkMap[aid];
  const activityQuery: Record<string, string> = { ...query,
    ...external_query,
    region,
    aid,
    u_code,
    enter_from
  };
  const schemaQuery = {
    url: `${window.location.origin}/${activity_url}?${querystringify(activityQuery)}`
  };
  const onelinkQuery = {
    af_dp: `${config.schema}?${querystringify(schemaQuery)}`,
    af_web_dp: 'https://www.tiktok.com',
    c: `UG_Referral_${region.toUpperCase()}`,
    is_retargeting: 'true',
    pid,
    gd_label,
    af_adset: invite_code ?? ''
  };
  return `${config.link}?${querystringify(onelinkQuery)}`;
};
export const isInViewport = (el?: HTMLElement | null) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.bottom >= 0 && rect.right >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight) && rect.left <= (window.innerWidth || document.documentElement.clientWidth);
};